class Nav {
	static USER = 1;
	static CIRCLE = 2;
	static COMMUNITY = 3;
	static TEAM = 4;
	static GUARDIANSHIP = 5;

	static linkRow(id, label, path) {
		return UI.a([
			UI.div('py-2 whitespace-nowrap', [
				UI.div('nav-icon', UI.icon(id, 20)),
				UI.span('text-sm align-middle', label)
			])
		], null, {
			'href': path,
			'id': 'nav-' + id
		})
	}

	static clickRow(id, label, clickFn) {
		return UI.div('py-2 whitespace-nowrap cursor-pointer', [
			UI.div('nav-icon', UI.icon(id, 20)),
			UI.span('text-sm align-middle', label)
		], { 'click': clickFn });
	}

	constructor (container) {
		this.container = container;

		this._params = new URLSearchParams(window.location.search);

		this.circles = new Circles(this);
		this.communities = new Communities(this);

		this.parse(units);
		this.userPermissions = Permissions.currentUser();

		this._selectedUnitType = null;
		this._selectedUnitId = null;
		if (this._params.has('circle')) {
			this._selectedUnitType = Nav.CIRCLE;
			this._selectedUnitId = this._params.get('circle');
			this._saveUnitCookie();
		} else if (this._params.has('community')) {
			this._selectedUnitType = Nav.COMMUNITY;
			this._selectedUnitId = this._params.get('community');
			this._saveUnitCookie();
		} else if (this._params.has('center')) {
			const centerId = this._params.get('center');
			const circle = this.circles.circles.find(c => c.center == centerId && !c.own && c.isGuardian);
			if (circle) {
				this._selectedUnitType = Nav.GUARDIANSHIP;
				this._selectedUnitId = circle.id;
				this._saveUnitCookie();
			}
		} else {
			this._loadUnitCookie();
		}

		this.container.appendChild(
			this.list = UI.div(null, [
				Nav.linkRow('dashboard', _('Dashboard'), '/dashboard'),
				this.userPermissions.can(Permissions.MANAGE_OWN_LAYERS) ? Nav.linkRow('layers', _('My Layers'), '/layers') : null,
				this.userPermissions.can(Permissions.MANAGE_OWN_RESOURCES) ? Nav.linkRow('resources', _('My Resources'), '/resources') : null,
				this.userPermissions.can(Permissions.MANAGE_OWN_GRAPHICS) ? Nav.linkRow('graphics', _('My Graphics'), '/graphics') : null,
				Nav.linkRow('globe-stand', _('My Spaces'), '/spaces'),
				UI.htm('hr'),
				this.unitSelector = this._buildUnitSelector(),
				this.unitContent = UI.div(null, this._buildUnitContent())
			])
		);
	}

	get page() {
		return window.location.pathname.split('/')[1];
	}
	
	get entityType() {
		if (this._params.has('center')) return Nav.USER;
		if (this._params.has('circle')) return Nav.CIRCLE;
		if (this._params.has('community')) return Nav.COMMUNITY;
		return Nav.USER;
	}

	get entityId() {
		if (this._params.has('center')) return this._params.get('center');
		if (this._params.has('circle')) return this._params.get('circle');
		if (this._params.has('community')) return this._params.get('community');
		return null;
	}
	
	get activeCircle() {
		return this.entityType == Nav.CIRCLE ? this.entityId : null;
	}

	get activeCommunity() {
		return this.entityType == Nav.COMMUNITY ? this.entityId : null;
	}

	get unit() {
		if (this._params.has('circle')) return this.circles.getCircle(this._params.get('circle'));
		if (this._params.has('community')) return this.communities.getCommunity(this._params.get('community'));
		return false;
	}

	get displayUnit() {
		if (this._selectedUnitType === Nav.CIRCLE) return this.circles.getCircle(this._selectedUnitId);
		if (this._selectedUnitType === Nav.GUARDIANSHIP) return this.circles.getCircle(this._selectedUnitId);
		if (this._selectedUnitType === Nav.COMMUNITY) return this.communities.getCommunity(this._selectedUnitId);
		return false;
	}

	get isCircle() {
		return this._selectedUnitType === Nav.CIRCLE;
	}

	userCan(permission) {
		return !this.unit || this.unit.memberCan(permission);
	}

	params(params) {
		params = params ? new URLSearchParams(params) : new URLSearchParams();
		if (this._params.has('center')) params.set('center', this._params.get('center'));
		if (this._params.has('circle')) params.set('circle', this._params.get('circle'));
		if (this._params.has('community')) params.set('community', this._params.get('community'));
		return params;
	}

	parse(data) {
		data.map(data => {
			if (data.unit_type == Nav.CIRCLE) {
				this.circles.add(Circle.fromData(data));
			}
			else if (data.unit_type == Nav.COMMUNITY) {
				this.communities.add(Community.fromData(data, this));
			}
		})
	}

	selectUnit(type, id) {
		this._selectedUnitType = type;
		this._selectedUnitId = id;
		this._saveUnitCookie();

		const unit = this.displayUnit;

		// If on a unit/center page, navigate to the equivalent page on the new unit
		if (this._params.has('center') || this._params.has('circle') || this._params.has('community')) {
			let url = null;
			if (unit) {
				if (type === Nav.GUARDIANSHIP) {
					url = unit.centerUrlForPage(this.page) ?? `/spaces?${unit.centerParams}`;
				} else {
					url = unit.urlForPage(this.page) ?? `/members?${unit.params}`;
				}
			}
			window.location.href = url ?? '/dashboard';
			return;
		}

		// Top-section page ("My …"): just update the nav display in place
		if (type === Nav.GUARDIANSHIP) {
			this._selectorIcon.replaceChildren(UI.icon('shield', 24));
			this._selectorLabel.textContent = unit ? unit.ownerName : '';
		} else {
			this._selectorIcon.replaceChildren(UI.icon(type === Nav.CIRCLE ? 'circle' : 'community', 24));
			this._selectorLabel.textContent = unit ? unit.name : '';
		}

		this.unitContent.innerHTML = '';
		if (unit) {
			const links = type === Nav.GUARDIANSHIP ? unit.getGuardianNavLinks() : unit.getNavLinks();
			for (const el of links) {
				if (el) this.unitContent.appendChild(el);
			}
		}
	}

	_saveUnitCookie() {
		let prefix;
		if (this._selectedUnitType === Nav.CIRCLE) prefix = 'c';
		else if (this._selectedUnitType === Nav.GUARDIANSHIP) prefix = 'g';
		else prefix = 'm';
		document.cookie = `selected_unit=${prefix}:${this._selectedUnitId};path=/;max-age=${60 * 60 * 24 * 365}`;
	}

	_loadUnitCookie() {
		const match = document.cookie.match(/(?:^|;\s*)selected_unit=([^;]+)/);
		if (!match) return;
		const [prefix, id] = match[1].split(':');
		if (prefix === 'c') { this._selectedUnitType = Nav.CIRCLE; this._selectedUnitId = id; }
		else if (prefix === 'g') { this._selectedUnitType = Nav.GUARDIANSHIP; this._selectedUnitId = id; }
		else if (prefix === 'm') { this._selectedUnitType = Nav.COMMUNITY; this._selectedUnitId = id; }
	}

	_buildUnitSelector() {
		const unit = this.displayUnit;
		let icon, label;
		if (!unit) {
			icon = 'angle-down';
			label = _('Select a unit');
		} else if (this._selectedUnitType === Nav.GUARDIANSHIP) {
			icon = 'shield';
			label = unit.ownerName;
		} else {
			icon = 'circle_id' in unit ? 'circle' : 'community';
			label = unit.name;
		}
		return UI.div('mx-1 my-2 text-white-700 cursor-pointer overflow-hidden', [
			UI.div('px-1 py-1 whitespace-nowrap flex flex-row items-center justify-between', [
				this._selectorIcon = UI.div('unit-selector-icon flex-0', UI.icon(icon, 24)),
				this._selectorLabel = UI.span('text-sm align-middle flex-1 truncate', label),
				UI.span('align-middle mx-2 flex-0', UI.icon('angle-down', 26))
			], null, {
				'id': 'unit-selector'
			})
		], {
			'click': () => this._unitPicker().handle(this.unitSelector)
		});
	}

	_buildUnitContent() {
		const unit = this.displayUnit;
		if (!unit) return [];
		return this._selectedUnitType === Nav.GUARDIANSHIP
			? unit.getGuardianNavLinks()
			: unit.getNavLinks();
	}

	_unitPicker() {
		if (!this.__picker) {
			this.__picker = Dialog.Popup(
				UI.div('grid grid-col-1 divide-y divide-grey-500', [
					...this.circles.pickerRows(),
					...this.communities.pickerRows()
				]),
				'below-start'
			);
		}
		return this.__picker;
	}
}